import CKEditorMaster from '@/components/CKEditor/CKEditorMaster';
import CustomLabel from '@/components/custom-label';
import {
  getReceiverOptions,
  convertTargetGroupsToOptions
} from '@/constants/notification.constant';
import { NotificationPermissionMode } from '@/enums/mode.enum';
import { useQuery } from '@/hooks/query.hook';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { watchUser } from '@/redux/slices/auth.slice';
import {
  createEmailNotification,
  getEmailNotificationDetail,
  resetNotificationDetail,
  updateEmailNotification,
  watchDetailLoading,
  watchLoadingNotification,
  watchEmailNotificationDetail,
  getTargetGroups,
  watchTargetGroups
} from '@/redux/slices/notification.slice';
import {
  ArrowLeftOutlined,
  CaretRightOutlined,
  CloseCircleOutlined,
  InfoCircleFilled
} from '@ant-design/icons';
import { css } from '@emotion/react';
import {
  App,
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Select,
  Typography
} from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';

export default function NotificationCreateEmail() {
  const { modal } = App.useApp();
  const [form] = Form.useForm();
  const [targetGroupValue, setTargetGroupValue] = useState<number[]>([]);
  const [editorKey, setEditorKey] = useState<number>(0);
  const user = useAppSelector(watchUser);

  const dispatch = useAppDispatch();
  const { mode, id } = useQuery();

  const emailNotificationDetail = useAppSelector(watchEmailNotificationDetail);
  const loading = useAppSelector(watchDetailLoading);
  const loadingNotification = useAppSelector(watchLoadingNotification);

  const isEditMode = mode === NotificationPermissionMode.EDITEMAIL && id;
  const notificationId = id ? Number(id) : null;

  const targetGroups = useAppSelector(watchTargetGroups);


  const targetGroupOptions = convertTargetGroupsToOptions(targetGroups);
  const receiverOptions = getReceiverOptions();

  const handleGoBack = () => {
    window.history.back();
  };

  const showCancelConfirmModal = () => {
    modal.confirm({
      icon: (
        <InfoCircleFilled
          style={{
            color: '#FAAD14'
          }}
        />
      ),
      title: 'Bạn có chắc chắn xóa thông báo này không?',
      content: (
        <em>
          Khi hủy thông báo này thì toàn bộ dữ liệu đã tạo sẽ bị xóa.
        </em>
      ),
      okText: 'Xóa',
      cancelText: 'Hủy',
      centered: true,
      className: 'custom-modal-center-button',
      onOk: () => {
        handleGoBack();
      },
      onCancel: () => {
      }
    });
  };

  const handleSubmit = (values: any) => {
    if (!user?.id) return;

    const sendDateTime = values.sendDate && values.sendTime
      ? dayjs(values.sendDate)
          .hour(values.sendTime.hour())
          .minute(values.sendTime.minute())
          .second(0)
          .format('YYYY-MM-DD HH:mm:ss')
      : '';

    if (isEditMode && notificationId) {
      const updatePayload: UpdateEmailNotificationPayload = {
        ID: notificationId,
        TITLE: values.title,
        CONTENT: values.content,
        TARGET_TYPE: values.recipients,
        TARGET_GROUP: values.targetGroup || [],
        SEND_DATE: sendDateTime,
        UPDATED_BY: user.id
      };
      
      dispatch(updateEmailNotification(updatePayload)).then(() => {
        handleGoBack();
      });
    } else {
      const createPayload: CreateEmailNotificationPayload = {
        TITLE: values.title,
        CONTENT: values.content,
        TARGET_TYPE: values.recipients,
        TARGET_GROUP: values.targetGroup || [],
        SEND_DATE: sendDateTime,
        CREATED_BY: user.id
      };
      
      dispatch(createEmailNotification(createPayload)).then(() => {
        handleGoBack();
      });
    }
  };

  useEffect(() => {
    if (targetGroups.length === 0) {
      dispatch(getTargetGroups());
    }
  }, [dispatch, targetGroups.length]);

  useEffect(() => {
    if (isEditMode && notificationId) {
      dispatch(getEmailNotificationDetail(notificationId));
    }
  }, [dispatch, isEditMode, notificationId]);

  useEffect(() => {
    if (emailNotificationDetail && isEditMode) {
      const sendDate = emailNotificationDetail.SEND_DATE 
        ? dayjs(emailNotificationDetail.SEND_DATE) 
        : null;

      form.setFieldsValue({
        title: emailNotificationDetail.TITLE,
        content: emailNotificationDetail.CONTENT,
        sendDate: sendDate,
        sendTime: sendDate,
        recipients: emailNotificationDetail.TARGET_TYPE,
        targetGroup: emailNotificationDetail.TARGET_GROUP
          ? emailNotificationDetail.TARGET_GROUP.map((group) => group.ID)
          : []
      });

      setTargetGroupValue(
        emailNotificationDetail.TARGET_GROUP?.map((group) => group.ID) || []
      );

      setEditorKey(prev => prev + 1);
    }
  }, [emailNotificationDetail, isEditMode, form]);

  useEffect(() => {
    return () => {
      form.resetFields();
      setTargetGroupValue([]);
      setEditorKey(0);
      dispatch(resetNotificationDetail());
    };
  }, [form, dispatch]);

  return (
    <div css={rootStyles}>
      <style>
        {`
          .custom-modal-center-button .ant-modal-confirm-btns {
            display: flex !important;
            justify-content: center !important;
            gap: 1rem !important;
          }

          .custom-modal-center-button .ant-btn-primary {
            margin-left: 0 !important;
          }

          .custom-modal-center-button .ant-modal-confirm-content {
            font-style: italic !important;
          }
        `}
      </style>
      <Row css={boxStyles} justify='space-between'>
        <Typography.Title level={5} style={{ marginBottom: 0, fontWeight: 700 }}>
          {isEditMode ? 'Cập nhật thông báo' : 'Tạo mới thông báo'} Email
        </Typography.Title>

        <Button
          type='text'
          icon={<ArrowLeftOutlined />}
          onClick={handleGoBack}
        />
      </Row>

      <Form
        form={form}
        layout='vertical'
        onFinish={handleSubmit}
        css={formStyles}
      >
        {/* Thời gian gửi Section */}
        <div css={sectionStyles}>
          <Row gutter={[8, 0]} align='bottom'>
            <Col span={4}>
              {CustomLabel({ text: 'Thời gian gửi', isRequired: true })}
            </Col>
            <Col span={5}>
              <Form.Item
                name='sendDate'
                rules={[
                  {
                    required: true,
                    message: 'Vui lòng chọn ngày gửi thông báo'
                  },
                  {
                    validator: (_, value) => {
                      if (!value) {
                        return Promise.resolve();
                      }
                      const today = dayjs().startOf('day');
                      const selectedDate = dayjs(value).startOf('day');

                      if (selectedDate.isBefore(today)) {
                        return Promise.reject(
                          new Error('Ngày gửi không được là ngày trong quá khứ')
                        );
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
                style={{ marginBottom: 0 }}
              >
                <DatePicker
                  placeholder='Chọn ngày gửi thông báo'
                  format={'DD/MM/YYYY'}
                  style={{ width: '100%' }}
                  disabledDate={(current) => {
                    return current && current < dayjs().startOf('day');
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={5}>
              <Form.Item
                style={{ marginBottom: 0 }}
                shouldUpdate={(prev, cur) => prev.sendDate !== cur.sendDate}
                dependencies={['sendDate']}
              >
                {({ getFieldValue }) => {
                  const sendDate = getFieldValue('sendDate');

                  return (
                    <Form.Item
                      name='sendTime'
                      dependencies={['sendDate']}
                      style={{ marginBottom: 0 }}
                      rules={[
                        {
                          required: true,
                          message: 'Vui lòng chọn giờ gửi thông báo'
                        },
                        ({ getFieldValue }) => ({
                          validator: (_, value) => {
                            if (!value) return Promise.resolve();

                            const sendDate = getFieldValue('sendDate');
                            if (!sendDate) return Promise.resolve();

                            const today = dayjs().startOf('day');
                            const selectedDate = dayjs(sendDate).startOf('day');

                            if (selectedDate.isSame(today)) {
                              const currentTime = dayjs();
                              const selectedDateTime = dayjs(sendDate)
                                .hour(value.hour())
                                .minute(value.minute());
                              if (selectedDateTime.isBefore(currentTime)) {
                                return Promise.reject(
                                  new Error('Giờ gửi không được là giờ trong quá khứ')
                                );
                              }
                            }

                            return Promise.resolve();
                          }
                        })
                      ]}
                    >
                      <DatePicker.TimePicker
                        key={sendDate ? sendDate.format('YYYY-MM-DD') : 'default'}
                        placeholder='Chọn giờ gửi thông báo'
                        format='HH:mm'
                        style={{ width: '100%' }}
                        disabledTime={() => {
                          if (!sendDate) return {};

                          const today = dayjs().startOf('day');
                          const selectedDate = dayjs(sendDate).startOf('day');

                          if (selectedDate.isSame(today)) {
                            const currentHour = dayjs().hour();
                            const currentMinute = dayjs().minute();

                            return {
                              disabledHours: () =>
                                Array.from({ length: currentHour }, (_, i) => i),
                              disabledMinutes: (hour: number) =>
                                hour === currentHour
                                  ? Array.from({ length: currentMinute + 1 }, (_, i) => i)
                                  : []
                            };
                          }

                          return {};
                        }}
                      />
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </Col>
          </Row>
        </div>

        {/* Nội dung thông báo Section */}
        <div css={sectionStyles}>
          <Typography.Title level={5} style={{ marginBottom: 0, fontWeight: 700 }}>
            Nội dung thông báo
          </Typography.Title>

          <Row gutter={16}>
            <Col span={4}>
              {CustomLabel({ text: 'Tiêu đề email', isRequired: true })}
            </Col>
            <Col span={10}>
              <Form.Item
                name='title'
                rules={[{ required: true, message: 'Vui lòng nhập tiêu đề' }]}
                style={{ marginBottom: 0 }}
              >
                <Input
                  placeholder='Nhập tiêu đề email'
                  style={{ padding: '8px 12px', width: '95%' }}
                  allowClear
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={4}>
              {CustomLabel({ text: 'Nội dung email', isRequired: true })}
            </Col>
            <Col span={24}>
              <Form.Item
                name='content'
                rules={[{ required: true, message: 'Vui lòng nhập nội dung' }]}
              >
                <div css={emailCKEditorStyles}>
                  <CKEditorMaster
                    key={`email-editor-${editorKey}`}
                    content={form.getFieldValue('content') || null}
                    placeholder='Nhập nội dung email'
                    handleUpdateContent={(data) => {
                      form.setFieldsValue({ content: data });
                    }}
                  />
                </div>
              </Form.Item>
            </Col>
          </Row>
        </div>

        {/* Danh sách người nhận Section */}
        <div css={sectionStyles}>
          <Typography.Title level={5} style={{ marginBottom: 0, fontWeight: 700 }}>
            Danh sách người nhận
          </Typography.Title>

          <Row gutter={16}>
            <Col span={3}>
              {CustomLabel({ text: 'Người nhận', isRequired: true })}
            </Col>
            <Col span={6}>
              <Form.Item
                name='recipients'
                rules={[
                  { required: true, message: 'Vui lòng chọn người nhận' }
                ]}
                initialValue={'ALL'}
              >
                <Select defaultActiveFirstOption options={receiverOptions} allowClear style={{ width: '60%' }}/>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => {
              const receiverValue = getFieldValue('recipients');

              if (receiverValue === 'ALL') {
                return (
                  <div>
                    <CaretRightOutlined /> Khớp{' '}
                    <strong>tất cả người dùng</strong>
                  </div>
                );
              }

              return (
                <Form.Item
                  name='targetGroup'
                  label='Nhập các điều kiện để chọn đối tượng gửi thông báo'
                  labelCol={{ span: 10 }}
                  wrapperCol={{ span: 6 }}
                >
                  <div
                    css={
                      targetGroupValue.length > 0
                        ? null
                        : ContainerSelectHealthStatus
                    }
                  >
                    <Select
                      css={selectStyleHealthStatus}
                      mode='multiple'
                      allowClear
                      options={targetGroupOptions}
                      placeholder='Chọn Trạng thái sức khỏe'
                      style={{ width: '95%' }}
                      value={targetGroupValue}
                      onChange={(value) => {
                        setTargetGroupValue(value || []);
                        form.setFieldValue('targetGroup', value || []);
                      }}
                      removeIcon={
                        <CloseCircleOutlined
                          style={{ color: '#1890FF', fontSize: '1.9rem' }}
                        />
                      }
                    />
                  </div>
                </Form.Item>
              );
            }}
          </Form.Item>
        </div>

        {/* Submit Buttons */}
        <div css={submitButtonsStyles}>
          <Button
            type='default'
            onClick={isEditMode ? handleGoBack : showCancelConfirmModal}
            style={{
              width: '25%'
            }}
          >
            Hủy
          </Button>
          <Button
            type='primary'
            htmlType='submit'
            loading={loading || loadingNotification}
            style={{
              width: '25%'
            }}
          >
            {isEditMode ? 'Cập nhật' : 'Lưu'}
          </Button>
        </div>
      </Form>
    </div>
  );
}

const rootStyles = css`
  background: var(--gray-soft-color);
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  padding: 1.4rem 2.4rem;
  min-height: calc(100vh - var(--header-height));
`;

const boxStyles = css`
  padding: 1rem;
  background: var(--white-color);
  box-shadow: 0.02rem 0.04rem 0.2rem var(--gray-light-color);
  &:nth-of-type(2) {
    padding: 1.6rem 1rem;
  }
`;

const formStyles = css`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const sectionStyles = css`
  padding: 0.4rem 0.8rem;
  background: var(--white-color);
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const submitButtonsStyles = css`
  display: flex;
  justify-content: center;
  gap: 1rem;
  padding: 1.6rem 1rem;
  background: var(--white-color);
  box-shadow: 0.02rem 0.04rem 0.2rem var(--gray-light-color);
`;

const ContainerSelectHealthStatus = css`
  border: 1px solid #00000040;
  border-radius: 0.5rem;
  padding: 0.8rem 1.6rem;

  .ant-select-selection-item {
    background: #ffffff !important;
    border-radius: 0.2rem !important;
    border: 1px solid #d9d9d9 !important;

    .ant-select-selection-item-content {
    }
  }
`;

const selectStyleHealthStatus = css`
  .ant-select-selection-item {
    background: #ffffff !important;
    border-radius: 0.2rem !important;
    border: 1px solid #d9d9d9 !important;

    .ant-select-selection-item-content {
      font-size: 1.2rem !important;
    }
  }
`;

const emailCKEditorStyles = css`
  margin-top: 0.8rem;
  ul li,
  ol li,
  nav ul {
    list-style: revert !important;
  }

  .ck.ck-content.ck-editor__editable.ck-rounded-corners.ck-editor__editable_inline {
    padding: 0.5rem 2.8rem;
  }
`;
